import React, { useState } from 'react';
import axios from 'axios';
import { endpoints } from '../api/api';
import supportTopics, { SupportTopic as Topic } from '../config/supportTopics';
import FeedbackModal from './FeedbackModal';

const TOPICS = supportTopics;

type Step = 'topics' | 'answer' | 'message' | 'success';

const SupportChatWidget: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [step, setStep] = useState<Step>('topics');
  const [selected, setSelected] = useState<Topic | null>(null);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleTopicSelect = (topic: Topic) => {
    setSelected(topic);
    setMessage('');
    setError(null);
    if (topic.answer) {
      setStep('answer');
    } else {
      setStep('message');
    }
  };

  const handleStillNeedHelp = () => {
    setStep('message');
  };

  const handleSubmit = async () => {
    if (!message.trim() || !selected) return;
    setSubmitting(true);
    setError(null);
    try {
      await axios.post(endpoints.createSupportTicket, {
        topic: selected.label,
        message: message.trim(),
      });
      setStep('success');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setTimeout(() => {
      setStep('topics');
      setSelected(null);
      setMessage('');
      setError(null);
    }, 300);
  };

  const handleBack = () => {
    setError(null);
    if (step === 'message' && selected?.answer) {
      setStep('answer');
    } else {
      setStep('topics');
      setSelected(null);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
      {showFeedback && <FeedbackModal type="general" onClose={() => setShowFeedback(false)} />}
      {/* Chat panel */}
      {open && (
        <div className="w-80 bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden">

          {/* Header */}
          <div className="bg-gray-900 px-5 py-4 flex items-center justify-between">
            <div>
              <p className="text-white font-semibold text-sm">Help & Support</p>
              <p className="text-gray-400 text-xs mt-0.5">We usually respond within a day</p>
            </div>
            <button onClick={handleClose} className="text-gray-400 hover:text-white transition-colors" aria-label="Close">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 px-5 py-4">

            {/* Step: Topics */}
            {step === 'topics' && (
              <div>
                <p className="text-gray-700 text-sm font-medium mb-3">What do you need help with?</p>
                <div className="flex flex-col gap-2">
                  {TOPICS.map((topic) => (
                    <button
                      key={topic.label}
                      onClick={() => handleTopicSelect(topic)}
                      className="text-left text-sm text-gray-700 bg-gray-50 hover:bg-orange-50 hover:text-orange-600 border border-gray-200 hover:border-orange-200 rounded-lg px-3 py-2.5 transition-colors"
                    >
                      {topic.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step: Pre-defined Answer */}
            {step === 'answer' && selected && (
              <div>
                <button onClick={handleBack} className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 mb-3 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                  Back
                </button>
                <p className="text-gray-900 text-sm font-medium mb-3">{selected.label}</p>
                <div className="bg-gray-50 rounded-xl px-4 py-3 mb-4">
                  <p className="text-gray-600 text-sm leading-relaxed">{selected.answer}</p>
                </div>
                <p className="text-gray-400 text-xs mb-2 text-center">Did this help?</p>
                <div className="flex gap-2">
                  <button
                    onClick={handleClose}
                    className="flex-1 bg-gray-900 text-white text-sm font-medium py-2 rounded-lg hover:bg-gray-800 transition-colors"
                  >
                    Yes, thanks!
                  </button>
                  <button
                    onClick={handleStillNeedHelp}
                    className="flex-1 bg-white border border-gray-200 text-gray-600 text-sm font-medium py-2 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Still need help
                  </button>
                </div>
              </div>
            )}

            {/* Step: Message */}
            {step === 'message' && (
              <div>
                <button onClick={handleBack} className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 mb-3 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                  Back
                </button>
                <p className="text-gray-700 text-sm font-medium mb-1">{selected?.label}</p>
                <p className="text-gray-400 text-xs mb-3">Describe your issue and we'll get back to you.</p>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={4}
                  placeholder="Type your message..."
                  className="w-full text-sm text-gray-700 border border-gray-200 rounded-lg px-3 py-2.5 resize-none focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent placeholder-gray-300"
                />
                {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
              </div>
            )}

            {/* Step: Success */}
            {step === 'success' && (
              <div className="py-4 text-center">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-gray-900 font-semibold text-sm">Got it!</p>
                <p className="text-gray-500 text-xs mt-1 leading-relaxed">We've received your message and will get back to you soon.</p>
                <button onClick={handleClose} className="mt-4 text-xs text-gray-400 hover:text-gray-600 transition-colors">Close</button>
              </div>
            )}
          </div>

          {/* Footer — only on message step */}
          {step === 'message' && (
            <div className="px-5 pb-4">
              <button
                onClick={handleSubmit}
                disabled={submitting || !message.trim()}
                className="w-full bg-gray-900 text-white text-sm font-medium py-2.5 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Sending...' : 'Send message'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Feedback floating button */}
      <button
        onClick={() => { setOpen(false); setShowFeedback(true); }}
        className="bg-white hover:bg-orange-50 text-orange-500 border border-orange-200 rounded-full shadow-md flex items-center justify-center transition-colors"
        style={{ width: 52, height: 52 }}
        aria-label="Give Feedback"
        title="Give Feedback"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
        </svg>
      </button>

      {/* Support floating button */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="bg-gray-900 hover:bg-gray-700 text-white rounded-full shadow-lg flex items-center justify-center transition-colors"
        style={{ width: 52, height: 52 }}
        aria-label="Help & Support"
      >
        {open ? (
          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
        )}
      </button>
    </div>
  );
};

export default SupportChatWidget;
